class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('.predictive-search-results');
    this.language = this.dataset.language || '';
    this.hubUrl = this.dataset.hubUrl || '';
    this.reqType = this.querySelector('input[type="hidden"][name="type"]')?.value || 'product';

    this.input.addEventListener(
      'input',
      this.debounce(() => {
        this.onChange();
      }, 300).bind(this)
    );

    this.form = this.querySelector('form');
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.onSubmit();
    });
  }

  connectedCallback() {
    document.addEventListener('click', this.handleClickOutside.bind(this));
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm, this.reqType);
  }

  async onSubmit() {
    const searchTerm = this.input.value.trim();
    if (!searchTerm.length) {
      return;
    }

    // CS hub: pages only (final production behavior). Never product/collection redirects.
    if (this.hubUrl) {
      const pageUrl = await this.getFirstPageUrlIfValid(searchTerm);
      if (pageUrl) {
        window.location.assign(pageUrl);
        return;
      }
      const separator = this.hubUrl.includes('?') ? '&' : '?';
      window.location.assign(`${this.hubUrl}${separator}q=${encodeURIComponent(searchTerm)}`);
      return;
    }

    // Product/collection search redirects only apply to the main product search.
    if (this.reqType === 'product') {
      const redirectPath = this.findRedirectForQuery(searchTerm);
      if (redirectPath) {
        window.location.assign(this.withLanguagePrefix(redirectPath));
        return;
      }
    }

    if (this.reqType === 'product' && this.isPdpRedirectEnabled()) {
      const productUrl = await this.getFirstProductUrlIfValid(searchTerm);
      if (productUrl) {
        window.location.assign(productUrl);
        return;
      }
    }

    if (this.reqType === 'page') {
      const pageUrl = await this.getFirstPageUrlIfValid(searchTerm);
      if (pageUrl) {
        window.location.assign(pageUrl);
        return;
      }
    }

    this.form.submit();
  }

  findRedirectForQuery(query) {
    const settings = this.getRedirectSettings();
    const redirects = settings?.redirects || [];
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return null;

    for (const redirect of redirects) {
      if (!redirect.query) continue;

      const queries = redirect.query.split(',').map((entry) => entry.trim().toLowerCase());
      if (queries.includes(normalizedQuery)) {
        return redirect.path;
      }
    }

    const startsWithMatch = redirects.find((redirect) => {
      const rule = (redirect.query || '').split(',')[0].trim().toLowerCase();
      return rule && normalizedQuery.startsWith(rule);
    });

    return startsWithMatch?.path || null;
  }

  getRedirectSettings() {
    const searchRedirects = document.getElementById('search-driven-redirects-settings');
    if (!searchRedirects?.textContent) return null;

    try {
      return JSON.parse(searchRedirects.textContent);
    } catch (error) {
      console.error('Failed to parse search redirect settings:', error);
      return null;
    }
  }

  isPdpRedirectEnabled() {
    const settings = this.getRedirectSettings();
    return settings?.enablePdpRedirect !== false;
  }

  getLanguageFromUrl() {
    const match = window.location.pathname.match(/^\/([a-z]{2})(?:\/|$)/i);
    if (match && match[1] && match[1] !== 'en') {
      return match[1].toLowerCase();
    }
    return '';
  }

  withLanguagePrefix(path) {
    const language = this.getLanguageFromUrl();
    if (!language || path.startsWith('http') || path.startsWith(`/${language}/`)) {
      return path;
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `/${language}${normalizedPath}`;
  }

  // Shopify search appends tracking params (_pos, _psq, _sid, _ss, _v, etc.). Keep clean paths.
  stripSearchTrackingParams(url) {
    if (!url) return url;
    return String(url).split(/[?#]/)[0];
  }

  stripTrackingFromSuggestionLinks() {
    this.predictiveSearchResults.querySelectorAll('.autocomplete__item a[href]').forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;
      link.setAttribute('href', this.stripSearchTrackingParams(href));
    });
  }

  getProductNameBeforeDash(title) {
    if (!title) return '';
    const dashIndex = title.indexOf(' - ');
    return dashIndex > 0 ? title.substring(0, dashIndex).trim() : title.trim();
  }

  allProductsHaveSameName(products) {
    if (!products || products.length <= 1) return true;
    const firstProductName = this.getProductNameBeforeDash(products[0].title);
    return products.every((product) => this.getProductNameBeforeDash(product.title) === firstProductName);
  }

  async fetchProducts(query, limit = 15) {
    try {
      const searchParams = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': limit.toString(),
      });

      const response = await fetch(`/search/suggest.json?${searchParams.toString()}`, {
        credentials: 'same-origin',
      });

      if (!response.ok) return [];

      const data = await response.json();
      return (data?.resources?.results?.products || [])
        .filter((product) => product.available)
        .slice(0, limit);
    } catch (error) {
      console.warn('Predictive search: failed to fetch products', error);
      return [];
    }
  }

  async fetchPages(query, limit = 15) {
    try {
      const searchParams = new URLSearchParams({
        q: query,
        'resources[type]': 'page',
        'resources[limit]': limit.toString(),
        'resources[options][fields]': 'title,body',
      });

      const response = await fetch(`/search/suggest.json?${searchParams.toString()}`, {
        credentials: 'same-origin',
      });

      if (!response.ok) return [];

      const data = await response.json();
      return data?.resources?.results?.pages || [];
    } catch (error) {
      console.warn('Predictive search: failed to fetch pages', error);
      return [];
    }
  }

  async getFirstProductUrlIfValid(query) {
    const products = await this.fetchProducts(query, 15);
    if (!products.length) return null;

    const predictiveSearchResults = this.predictiveSearchResults.querySelectorAll(
      '#predictive-search-results .autocomplete__item'
    );

    if (predictiveSearchResults.length > products.length) {
      return null;
    }

    if (!this.allProductsHaveSameName(products)) {
      return null;
    }

    let productUrl = this.stripSearchTrackingParams(products[0].url || null);
    if (!productUrl) return null;

    return this.withLanguagePrefix(productUrl);
  }

  async getFirstPageUrlIfValid(query) {
    const pages = await this.fetchPages(query, 15);
    if (pages.length !== 1) return null;

    const predictiveSearchResults = this.predictiveSearchResults.querySelectorAll(
      '#predictive-search-results .autocomplete__item'
    );

    if (predictiveSearchResults.length > 1) {
      return null;
    }

    const pageUrl = this.stripSearchTrackingParams(pages[0].url || null);
    if (!pageUrl) return null;

    return this.withLanguagePrefix(pageUrl);
  }

  getSearchResults(searchTerm, reqType) {
    // CS hub always suggests pages only (final production behavior).
    const effectiveType = this.hubUrl ? 'page' : reqType;
    let urlParams = '';
    if (effectiveType === 'product') {
      urlParams =
        'resources[type]=product&resources[options][fields]=title,variants.title,variants.sku,product_type,tag';
    } else if (effectiveType === 'article') {
      urlParams = 'resources[type]=article';
    } else if (effectiveType === 'page') {
      urlParams = 'resources[type]=page&resources[options][fields]=title,body';
    }

    fetch(`/search/suggest?q=${searchTerm}&${urlParams}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        if (this.hubUrl) {
          this.predictiveSearchResults.querySelector('.autocomplete__no-products')?.remove();
          this.predictiveSearchResults.querySelector('.products__results')?.remove();
        }
        this.stripTrackingFromSuggestionLinks();
        this.updateViewAllLink(searchTerm);
        if (this.language && this.language !== 'en') this.modifyProductUrls();
        this.open();
      })
      .catch(() => {
        this.close();
      });
  }

  updateViewAllLink(searchTerm) {
    const viewAllLink = this.predictiveSearchResults.querySelector('.autocomplete__view-all a');
    if (!viewAllLink) return;

    // CS hub "View all" stays on the hub with page results — never product search.
    if (this.hubUrl) {
      const separator = this.hubUrl.includes('?') ? '&' : '?';
      viewAllLink.href = `${this.hubUrl}${separator}q=${encodeURIComponent(searchTerm)}`;
      return;
    }

    if (this.reqType === 'page') {
      viewAllLink.href = `/search?type=page&q=${encodeURIComponent(searchTerm)}`;
      return;
    }

    viewAllLink.href = `/search?type=product&q=${encodeURIComponent(searchTerm)}`;
  }

  open() {
    this.predictiveSearchResults.style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.predictiveSearchResults.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  modifyProductUrls() {
    const hostname = window.location.hostname;
    const isCanadianSite = hostname.includes('ca.nixon.com');

    if (!isCanadianSite) {
      return;
    }

    const languagePrefix = `/${this.language}`;
    const productLinks = this.predictiveSearchResults.querySelectorAll('a[href*="/products/"]');
    productLinks.forEach((link) => {
      const href = link.getAttribute('href');
      if (href && !href.startsWith(languagePrefix) && !href.startsWith('http')) {
        link.setAttribute('href', `${languagePrefix}${href}`);
      }
    });
  }
}

customElements.define('predictive-search', PredictiveSearch);

function initCustomerServiceHubSearch() {
  const query = new URLSearchParams(window.location.search).get('q');
  if (!query) return;

  const main = document.querySelector('.customer-service__main');
  if (!main) return;

  let container = main.querySelector('[data-cs-search-results]');
  if (!container) {
    container = document.createElement('div');
    container.className = 'customer-service__search-results';
    container.setAttribute('data-cs-search-results', '');
    main.appendChild(container);
  }

  container.dataset.searchQuery = query;
  main.classList.add('customer-service__main--search-active');

  const searchInput = document.querySelector('#customer-service-input');
  if (searchInput) {
    searchInput.value = query;
  }

  container.setAttribute('aria-busy', 'true');

  fetch(
    `/search?section_id=customer-service-search-results&type=page&q=${encodeURIComponent(query)}`,
    { credentials: 'same-origin' }
  )
    .then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    })
    .then((html) => {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const results = parsed.querySelector('.customer-service-search-results');
      container.innerHTML = results ? results.outerHTML : html;
    })
    .catch(() => {
      container.innerHTML =
        '<p class="customer-service-search-results__empty">Unable to load search results. Please try again.</p>';
    })
    .finally(() => {
      container.removeAttribute('aria-busy');
    });
}

document.addEventListener('DOMContentLoaded', initCustomerServiceHubSearch);
